import { IconAlertCircle } from '@myplayer/ui';
import { useEffect, useId, useState, type ReactElement } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { Button } from '../components/ui/primitives.js';

export interface FieldHintProps {
  /** Human-readable name of the field, e.g. "GTIN (14 digits)" */
  readonly label: string;
  /** Plain-English explanation written for a brand manager */
  readonly hint: string;
  /** Optional example value shown in monospace */
  readonly example?: string | undefined;
  /** Optional slug in /help/:slug for deeper reading */
  readonly helpSlug?: string | undefined;
  /** Optional visible button text; defaults to "What is this?" */
  readonly buttonText?: string | undefined;
  /** Optional test ID prefix */
  readonly testId?: string | undefined;
}

/**
 * Reusable "What is this?" inline field hint button and accessible popover card.
 * Uses only shared @myplayer/ui design tokens and the central icon wrapper.
 */
export function FieldHint({
  label,
  hint,
  example,
  helpSlug,
  buttonText = 'What is this?',
  testId,
}: FieldHintProps): ReactElement {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const inRouter = useInRouterContext();

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <span className="relative inline-flex items-center">
      <Button
        type="button"
        variant="secondary"
        size="xs"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={testId ? `${testId}-trigger` : undefined}
        onClick={() => setOpen((prev) => !prev)}
        className="rounded-pill"
      >
        <IconAlertCircle className="h-3.5 w-3.5" />
        <span>{buttonText}</span>
      </Button>

      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={`Explanation for ${label}`}
          data-testid={testId ? `${testId}-popover` : undefined}
          className="absolute left-0 top-full z-30 mt-1.5 w-72 rounded-card border border-border bg-surface p-3.5 text-left text-[12px] leading-relaxed text-fg shadow-primary-glow"
        >
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="font-semibold text-fg">{label}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              aria-label={`Close hint for ${label}`}
              onClick={() => setOpen(false)}
              className="text-fg-muted hover:bg-brand-wash hover:text-fg"
            >
              Close
            </Button>
          </div>

          <p className="text-fg-secondary">{hint}</p>

          {example !== undefined && example.trim().length > 0 && (
            <div className="mt-2 rounded-control border border-subtle bg-surface-muted px-2 py-1 font-mono text-[11px] text-fg">
              Example: {example}
            </div>
          )}

          {helpSlug !== undefined && helpSlug.trim().length > 0 && (
            <div className="mt-2 border-t border-subtle pt-1.5">
              {inRouter ? (
                <Link
                  to={`/help/${helpSlug}`}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline"
                >
                  Read more in the Help Centre
                </Link>
              ) : (
                <a
                  href={`/help/${helpSlug}`}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline"
                >
                  Read more in the Help Centre
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  );
}
